import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Sparkles,
  Save,
  Pencil,
  BookHeart,
} from "lucide-react";
import { Screen } from "@/components/Screen";
import { PageHeader } from "@/components/PageHeader";
import { ResultBadge } from "@/components/ResultBadge";
import { PremiumInlineTeaser } from "@/components/PremiumInlineTeaser";
import { useI18n } from "@/i18n/I18nProvider";
import {
  getCheckInByDate,
  getLatestCheckIn,
  updateReflectionNote,
} from "@/store/store";
import { todayLocalISO, formatDate } from "@/lib/date";

const MAX_NOTE = 600;

export default function Reflection() {
  const { t, language } = useI18n();
  const navigate = useNavigate();

  const todayISO = useMemo(() => todayLocalISO(), []);
  // Prefer today's entry; fall back to the latest one.
  const [entry, setEntry] = useState(
    () => getCheckInByDate(todayISO) || getLatestCheckIn(),
  );

  if (!entry) {
    return (
      <Screen testId="reflection-screen">
        <PageHeader
          titleKey="reflection.title"
          testId="reflection-header"
        />
        <EmptyState onCta={() => navigate("/today")} />
      </Screen>
    );
  }

  const isToday = entry.date === todayISO;
  const headerText = isToday
    ? t("reflection.headerToday")
    : t("reflection.headerOther", {
        date: formatDate(entry.date, language),
      });

  return (
    <Screen testId="reflection-screen">
      <PageHeader titleKey="reflection.title" testId="reflection-header" />

      <SummaryCard entry={entry} headerText={headerText} />

      <ReflectionEditor
        entry={entry}
        onSaved={(nextEntry) => setEntry(nextEntry)}
      />

      <PremiumInlineTeaser
        Icon={Sparkles}
        textKey="premium.reflectionTeaser"
        testId="reflection-premium-teaser"
      />
    </Screen>
  );
}

/* ------------------------------------------------------------------ */

function EmptyState({ onCta }) {
  const { t } = useI18n();
  return (
    <div
      className="dc-card relative overflow-hidden mt-6 p-6 flex flex-col items-center text-center"
      data-testid="reflection-empty"
    >
      <div
        className="absolute -top-14 -right-14 w-40 h-40 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(167,139,250,0.20) 0%, rgba(167,139,250,0) 70%)",
        }}
      />
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center relative"
        style={{
          background:
            "radial-gradient(circle, rgba(167,139,250,0.20) 0%, rgba(167,139,250,0) 70%)",
        }}
      >
        <BookHeart size={24} className="text-[#A78BFA]" strokeWidth={1.6} />
      </div>
      <p className="mt-4 text-[15px] text-[#CBD5E1] leading-relaxed max-w-[340px]">
        {t("reflection.empty.message")}
      </p>
      <button
        type="button"
        onClick={onCta}
        data-testid="reflection-empty-cta"
        className="group mt-5 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[14px] font-medium transition-all duration-300 shadow-[0_10px_30px_-15px_rgba(244,201,93,0.6)] hover:shadow-[0_14px_36px_-14px_rgba(244,201,93,0.8)] active:scale-[0.985]"
        style={{
          background: "linear-gradient(180deg, #F5D07A 0%, #F4C95D 100%)",
          color: "#0F172A",
          letterSpacing: "-0.005em",
        }}
      >
        {t("reflection.empty.cta")}
        <ArrowRight
          size={16}
          className="transition-transform duration-300 group-hover:translate-x-0.5"
          strokeWidth={2}
        />
      </button>
    </div>
  );
}

function SummaryCard({ entry, headerText }) {
  const { t, language } = useI18n();
  return (
    <div
      className="dc-card mt-6 p-5 flex items-center gap-4"
      data-testid="reflection-summary-card"
    >
      <ResultBadge result={entry.result} size={64} />
      <div className="flex-1 min-w-0">
        <p
          className="text-[11px] uppercase tracking-[0.16em] text-[#E8D8B8]/80"
          style={{ letterSpacing: "0.16em" }}
        >
          {headerText}
        </p>
        <h2
          className="mt-1 text-xl font-medium"
          style={{ letterSpacing: "-0.015em", color: "#F8FAFC" }}
          data-testid="reflection-result-title"
        >
          {t(`result.labels.${entry.result}.title`)}
        </h2>
        <p className="mt-0.5 text-xs text-[#94A3B8] capitalize">
          {formatDate(entry.date, language)}
        </p>
      </div>
    </div>
  );
}

function ReflectionEditor({ entry, onSaved }) {
  const { t, tArray } = useI18n();
  const questions = tArray(`reflection.questions.${entry.result}`);
  const hasNote = !!(entry.reflectionNote && entry.reflectionNote.trim());
  const [editing, setEditing] = useState(!hasNote);
  const [text, setText] = useState(entry.reflectionNote || "");

  const handleSave = () => {
    const value = text.trim();
    updateReflectionNote(entry.date, value);
    const updated = { ...entry, reflectionNote: value };
    toast.success(t("reflection.toastSaved"));
    setEditing(false);
    onSaved(updated);
  };

  const handleEdit = () => {
    setText(entry.reflectionNote || "");
    setEditing(true);
  };

  if (!editing && hasNote) {
    return (
      <div className="mt-4" data-testid="reflection-saved-view">
        <div className="dc-card p-5">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles size={12} className="text-[#F4C95D]/80" strokeWidth={1.8} />
            <p className="text-[10px] uppercase tracking-[0.16em] text-[#E8D8B8]/75">
              {t("reflection.saved.heading")}
            </p>
          </div>
          <p
            className="text-[15px] text-[#F8FAFC]/95 leading-relaxed whitespace-pre-wrap"
            data-testid="reflection-saved-text"
          >
            {entry.reflectionNote}
          </p>
        </div>
        <button
          type="button"
          onClick={handleEdit}
          data-testid="reflection-edit-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-white/80 border border-white/12 hover:bg-white/[0.05] hover:text-white transition-colors duration-200"
        >
          <Pencil size={14} strokeWidth={1.7} />
          {t("reflection.saved.edit")}
        </button>
      </div>
    );
  }

  const counterText = t("reflection.counter", { count: text.length });

  return (
    <div className="mt-4" data-testid="reflection-editor">
      <div className="dc-card p-5">
        <p className="text-[11px] uppercase tracking-[0.16em] text-[#E8D8B8]/80 mb-3">
          {t("reflection.questionsIntro")}
        </p>
        <ol className="space-y-2.5 mb-4" data-testid="reflection-questions">
          {questions.map((q, i) => (
            <li key={i} className="flex gap-2.5 text-[14px] text-[#F8FAFC]/90 leading-snug">
              <span
                className="flex-none text-[11px] font-medium text-[#F4C95D]/85 tracking-[0.14em] pt-0.5"
                style={{ letterSpacing: "0.14em" }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>{q}</span>
            </li>
          ))}
        </ol>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_NOTE))}
          placeholder={t("reflection.textareaPlaceholder")}
          rows={5}
          data-testid="reflection-textarea"
          className="w-full rounded-2xl bg-white/[0.03] border border-white/10 focus:border-[#A78BFA]/50 focus:outline-none text-[14px] text-[#F8FAFC] placeholder:text-[#94A3B8]/70 px-4 py-3 resize-none transition-colors duration-200"
          style={{ letterSpacing: "-0.005em" }}
        />
        <div className="mt-2 flex justify-end text-[11px] text-[#94A3B8]/80">
          {counterText}
        </div>
      </div>

      <button
        type="button"
        onClick={handleSave}
        data-testid="reflection-save-btn"
        className="group mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[14px] font-medium transition-all duration-300 shadow-[0_10px_30px_-15px_rgba(244,201,93,0.6)] hover:shadow-[0_14px_36px_-14px_rgba(244,201,93,0.8)] active:scale-[0.985]"
        style={{
          background: "linear-gradient(180deg, #F5D07A 0%, #F4C95D 100%)",
          color: "#0F172A",
          letterSpacing: "-0.005em",
        }}
      >
        <Save size={14} strokeWidth={1.9} />
        {t("reflection.saveButton")}
      </button>
    </div>
  );
}
